import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { TrendingUp, TrendingDown, Loader2, RefreshCw, ArrowUpDown, ArrowUp, ArrowDown, Star, Rocket } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import DividendFilterSegmentedControl from '@/components/DividendFilterSegmentedControl';
import SqueezeRadarPill from '@/components/SqueezeRadarPill';
import NewsBadge, { useNewsTickersFor } from '@/components/NewsBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * TopMoversWidget — Round 86 revision (Feb 2026).
 *
 * Behaviour changes from prior version (user feedback):
 *   • Both Gainers AND Losers are fetched in parallel on first load,
 *     not lazily on toggle — so flipping the toggle is INSTANT (no
 *     spinner, no wait). The 5–10s server hit for the alternate
 *     direction happens in the background while the user is reading
 *     the first list.
 *   • Auto-refresh (60s) updates BOTH lists silently. The visible
 *     list only changes if data actually changed.
 *   • Manual refresh forces both directions via `?force=1`.
 *   • localStorage cache (`dividrip:topMovers:cache`) keyed by
 *     trading_day persists both lists across page navigations, so
 *     the second page visit feels instant — even before the network
 *     reply lands.
 *   • Loading spinner ONLY shows on the very first page load when
 *     localStorage is empty AND no network reply has arrived yet.
 *
 * Preferences (direction toggle + 5/10 size) still persisted under
 * `dividrip:topMovers:prefs` for "feels-yours" continuity.
 *
 * Data-rendering:
 *   • Each row shows Ticker · Price · % Move · Volume · Yield
 *   • Non-DB tickers wear an amber "new" tag (auto-routed to admin
 *     pending-dividend-review queue server-side)
 *   • Click row → opens full Stock Modal via existing
 *     `handleStockClick(ticker)` (which already has the preview
 *     fallback for non-DB tickers — no cache write).
 */

const PREFS_KEY = 'dividrip:topMovers:prefs';
const CACHE_KEY = 'dividrip:topMovers:cache';

const loadPrefs = () => {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return {};
    const obj = JSON.parse(raw) || {};
    return {
      direction: obj.direction === 'losers' ? 'losers' : 'gainers',
      limit: [3, 5, 10].includes(obj.limit) ? obj.limit : 3,
      // Round 94c (Feb 2026) — `includeLeveraged` defaults to `true` so
      // existing users see the SAME list they did yesterday. Unchecking
      // it filters 2x/3x ETFs out of the visible 3/5/10.
      // `?? true` covers the legacy-prefs case where the key is missing.
      includeLeveraged: obj.includeLeveraged ?? true,
      dividendFilter:
        obj.dividendFilter === 'all' || obj.dividendFilter === 'non_dividend'
          ? obj.dividendFilter
          : 'dividend',
    };
  } catch { return {}; }
};

const loadCache = (filter) => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw) || null;
    if (!obj) return null;
    // Round 96e — cache is per-filter. If the stored cache was for a
    // different dividend_filter mode, ignore it on initial mount so
    // we don't flash stale data from the previous mode while the
    // fresh fetch is in flight.
    if (filter && obj.dividend_filter && obj.dividend_filter !== filter) return null;
    return obj;
  } catch { return null; }
};

const fmtPct = (n) => (n == null ? '—' : `${n >= 0 ? '+' : ''}${Number(n).toFixed(2)}%`);
const fmtPrice = (n) => (n == null ? '—' : `$${Number(n).toFixed(2)}`);
const fmtVol = (n) => {
  if (n == null) return '—';
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return `${n}`;
};
const fmtTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  } catch { return ''; }
};

/**
 * Returns true when current time is within 07:16 – 20:16 America/New_York
 * (Mon-Fri only). Mirrors the backend's `in_live_window` so the frontend
 * doesn't auto-poll outside the window (manual refresh still works).
 *
 * The 16-minute offsets match the 15-min-delayed Indices feed plus 1
 * minute of safety so we don't poll for data Massive hasn't published yet.
 */
const isInLiveWindow = () => {
  try {
    // Intl gives us the ET clock as numeric parts in one call.
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      hour12: false,
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }).formatToParts(new Date());
    const get = (t) => parts.find((p) => p.type === t)?.value;
    const wk = get('weekday'); // Mon, Tue, ...
    if (wk === 'Sat' || wk === 'Sun') return false;
    const h = parseInt(get('hour'), 10);
    const m = parseInt(get('minute'), 10);
    const mins = h * 60 + m;
    const open = 7 * 60 + 16;   // 07:16
    const close = 20 * 60 + 16; // 20:16
    return mins >= open && mins < close;
  } catch {
    // If timezone parsing fails for any reason, fall back to polling.
    // Better to make extra calls than to never refresh for everyone.
    return true;
  }
};

export default function TopMoversWidget({ onStockClick, watchlist = [], onToggleWatchlist }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const initialPrefs = loadPrefs();
  const [direction, setDirection] = useState(initialPrefs.direction || 'gainers');
  const [limit, setLimit] = useState(initialPrefs.limit || 3);
  // Round 95z — 3-way dividend filter for the movers widget.
  // SEPARATE from the Stock Table's filter — both default to
  // "dividend" but track independently. The two non-default modes are
  // admin-gated; non-admin users see the locked pills (or the backend
  // silently downgrades the request to "dividend" if anything sneaks
  // through). Persisted to localStorage so reloads remember choice.
  const [dividendFilter, setDividendFilter] = useState(() => {
    const stored = initialPrefs.dividendFilter;
    if (stored === 'all' || stored === 'non_dividend') {
      // Only honor the stored non-default value if the user is admin.
      // For non-admin (or anonymous) we always reset to "dividend".
      return (user?.role === 'admin') ? stored : 'dividend';
    }
    return 'dividend';
  });
  // Round 96e — cache is keyed by filter; only hydrate from cache if
  // it was stored for the same filter we're rendering now.
  const initialCache = loadCache(
    (initialPrefs.dividendFilter === 'all' || initialPrefs.dividendFilter === 'non_dividend')
      ? ((user?.role === 'admin') ? initialPrefs.dividendFilter : 'dividend')
      : 'dividend'
  );
  // Round 94c (Feb 2026) — Leveraged-toggle. Checked by default (so
  // existing 2x/3x ETF heavy lists stay unchanged). When unchecked the
  // visible 3/5/10 list excludes any row flagged `is_leveraged: true`
  // by the backend. We always fetch limit=30 from the server so even
  // a Top-10 view has enough non-leveraged headroom after filtering.
  const [includeLeveraged, setIncludeLeveraged] = useState(initialPrefs.includeLeveraged ?? true);
  // Round 89g — column-sortable table. Default key is `null` which
  // preserves the server's natural ranking (biggest mover at the top
  // for gainers, biggest dropper at the top for losers). Clicking a
  // header toggles asc → desc → null (clear). Direction toggle does
  // NOT reset the sort — user's column choice persists across the
  // gainers/losers flip.
  const [sortBy, setSortBy] = useState(null); // 'ticker' | 'price' | 'change_pct' | 'volume' | 'dividend_yield'
  const [sortDir, setSortDir] = useState('desc'); // 'asc' | 'desc'
  // `data` holds movers for BOTH directions so toggling is instant.
  const [data, setData] = useState({
    gainers: initialCache?.gainers?.movers || null,
    losers: initialCache?.losers?.movers || null,
  });
  const visibleTickers = useMemo(
    () => [...(data.gainers || []), ...(data.losers || [])].map((m) => m.ticker),
    [data.gainers, data.losers],
  );
  const newsTickers = useNewsTickersFor(visibleTickers);
  const [fetchedAt, setFetchedAt] = useState({
    gainers: initialCache?.gainers?.fetched_at || null,
    losers: initialCache?.losers?.fetched_at || null,
  });
  const [marketStatus, setMarketStatus] = useState(initialCache?.market_status || null);
  const [refreshing, setRefreshing] = useState(false);
  // `firstLoad` is only true until the first network reply for the
  // currently-visible direction arrives. Used to gate the loading spinner.
  const [firstLoad, setFirstLoad] = useState(
    !initialCache?.gainers?.movers && !initialCache?.losers?.movers,
  );
  const forceFlagRef = useRef(false);
  const refreshIdxRef = useRef(0);
  // Round 96e — Track filter changes so we can clear stale data on
  // toggle. Without this, switching Dividend → Non-Dividend would
  // briefly show the previous filter's rows until the new fetch
  // returns (a confusing "did anything happen?" UX).
  const prevFilterRef = useRef(dividendFilter);

  // Clear cached rows the instant the filter changes so the user sees
  // a loading state — not stale data — while the new fetch is in
  // flight. Skips on first mount (prevFilterRef === dividendFilter).
  useEffect(() => {
    if (prevFilterRef.current === dividendFilter) return;
    prevFilterRef.current = dividendFilter;
    setData({ gainers: null, losers: null });
    setFetchedAt({ gainers: null, losers: null });
    setFirstLoad(true);
  }, [dividendFilter]);

  // Persist preferences across reloads.
  useEffect(() => {
    try {
      localStorage.setItem(PREFS_KEY, JSON.stringify({ direction, limit, includeLeveraged, dividendFilter }));
    } catch { /* swallow */ }
  }, [direction, limit, includeLeveraged, dividendFilter]);

  const fetchBoth = useCallback(async (opts = {}) => {
    const { signal, force } = opts;
    try {
      const r = await axios.get(`${API}/market/movers/both`, {
        // Round 94c (Feb 2026) — Pull 50 rows from the server so when
        // the user unchecks "Leveraged" we still have enough non-
        // leveraged headroom to fill a Top-10 view. (Some sessions the
        // leveraged tickers occupy >20 of the top 30, leaving fewer
        // than 10 non-leveraged for the user — bumped from 30 → 50.)
        // Round 95z — `dividend_filter` slices the snapshot universe
        // by pays_dividend flag. Default "dividend" matches pre-95z
        // behaviour; admin can flip to "all" or "non_dividend".
        params: { limit: 50, dividend_filter: dividendFilter, ...(force ? { force: 1 } : {}) },
        withCredentials: true,
        signal,
      });
      return r.data || null;
    } catch {
      return null;
    }
  }, [dividendFilter]);

  // Fetch BOTH directions in a SINGLE request on mount + manual refresh
  // + 60s auto-poll. One request = one Massive snapshot fetch on the
  // server (was previously two parallel requests = two snapshot fetches).
  useEffect(() => {
    const ctrl = new AbortController();
    let timerId = null;
    let cancelled = false;

    const doFetch = async (force) => {
      const useForce = force || forceFlagRef.current;
      forceFlagRef.current = false;
      if (useForce) setRefreshing(true);
      const resp = await fetchBoth({ signal: ctrl.signal, force: useForce });
      if (cancelled) return;
      const nextData = {
        gainers: resp?.gainers ?? null,
        losers: resp?.losers ?? null,
      };
      const nextFetchedAt = {
        gainers: resp?.fetched_at_gainers ?? null,
        losers: resp?.fetched_at_losers ?? null,
      };
      const nextStatus = resp?.market_status || null;
      setData(nextData);
      setFetchedAt(nextFetchedAt);
      setMarketStatus(nextStatus);
      setFirstLoad(false);
      setRefreshing(false);
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({
          gainers: { movers: nextData.gainers, fetched_at: nextFetchedAt.gainers },
          losers: { movers: nextData.losers, fetched_at: nextFetchedAt.losers },
          market_status: nextStatus,
          dividend_filter: dividendFilter,
          stored_at: new Date().toISOString(),
        }));
      } catch { /* swallow */ }
    };

    // First mount: if we already hydrated lists from localStorage AND
    // we're outside the live window, skip the initial network call —
    // the user can hit the refresh button to force a pull. Otherwise
    // fetch once to populate.
    const hasCached = (initialCache?.gainers?.movers && initialCache.gainers.movers.length)
      || (initialCache?.losers?.movers && initialCache.losers.movers.length);
    if (!(hasCached && !isInLiveWindow())) {
      doFetch(false);
    } else {
      // Cached data + outside window → mark loaded so we don't render the spinner.
      setFirstLoad(false);
    }
    // Round 86 — Auto-poll only fires inside 07:16-20:16 ET (Mon-Fri).
    // Outside the window, the list stays frozen at whatever was last
    // cached; users can still hit the manual refresh button to force a
    // fetch if they want to see overnight or weekend movement.
    timerId = setInterval(() => {
      if (!isInLiveWindow()) return;
      doFetch(false);
    }, 60_000);

    return () => {
      cancelled = true;
      ctrl.abort();
      if (timerId) clearInterval(timerId);
    };
  }, [dividendFilter, fetchBoth]);  // Round 95z — refetch when filter changes

  const handleManualRefresh = () => {
    forceFlagRef.current = true;
    setRefreshing(true);
    (async () => {
      const resp = await fetchBoth({ force: true });
      if (!resp) {
        setRefreshing(false);
        return;
      }
      const nextData = {
        gainers: resp?.gainers ?? data.gainers,
        losers: resp?.losers ?? data.losers,
      };
      const nextFetchedAt = {
        gainers: resp?.fetched_at_gainers ?? fetchedAt.gainers,
        losers: resp?.fetched_at_losers ?? fetchedAt.losers,
      };
      const nextStatus = resp?.market_status || marketStatus;
      setData(nextData);
      setFetchedAt(nextFetchedAt);
      setMarketStatus(nextStatus);
      setRefreshing(false);
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({
          gainers: { movers: nextData.gainers, fetched_at: nextFetchedAt.gainers },
          losers: { movers: nextData.losers, fetched_at: nextFetchedAt.losers },
          market_status: nextStatus,
          dividend_filter: dividendFilter,
          stored_at: new Date().toISOString(),
        }));
      } catch { /* swallow */ }
    })();
  };

  // Visible list = data[direction], capped to user's `limit`. May be null
  // briefly on first paint while gainers/losers fetch in parallel.
  // Round 89g — applies the user-selected sort (if any) BEFORE the
  // `slice(0, limit)` so the top-N are taken from the SORTED list,
  // not from the server's natural ranking. Clearing the sort (sortBy
  // null) returns to server order.
  // Round 94c — When `includeLeveraged` is false, leveraged 2x/3x rows
  // are stripped from the pool BEFORE sort + slice, so the visible
  // 3/5/10 are taken from the non-leveraged subset only. Server pulls
  // 30 rows so even Top-10 has enough headroom after filtering.
  const sortedMovers = useMemo(() => {
    let arr = [...(data[direction] || [])];
    if (!includeLeveraged) arr = arr.filter((r) => !r.is_leveraged);
    if (!sortBy) return arr;
    const mul = sortDir === 'asc' ? 1 : -1;
    arr.sort((x, y) => {
      const xv = x?.[sortBy];
      const yv = y?.[sortBy];
      // Nulls always sink to the bottom regardless of direction so the
      // visible top-N never wastes a row on "—".
      if (xv == null && yv == null) return 0;
      if (xv == null) return 1;
      if (yv == null) return -1;
      if (typeof xv === 'string' && typeof yv === 'string') {
        return xv.localeCompare(yv) * mul;
      }
      return (Number(xv) - Number(yv)) * mul;
    });
    return arr;
  }, [data, direction, sortBy, sortDir, includeLeveraged]);

  const visibleMovers = sortedMovers.slice(0, limit);
  const showSpinner = firstLoad && (!data[direction] || data[direction].length === 0);
  const showEmpty = !showSpinner && visibleMovers.length === 0;

  // Click cycle: null → desc → asc → null. For non-numeric columns
  // (Ticker), it's asc → desc → null since ascending alphabetical is
  // the more useful default.
  const onSortClick = (col, isText = false) => {
    if (sortBy !== col) {
      setSortBy(col);
      setSortDir(isText ? 'asc' : 'desc');
      return;
    }
    // Same column already selected → flip direction, then clear.
    if (sortDir === (isText ? 'asc' : 'desc')) {
      setSortDir(isText ? 'desc' : 'asc');
    } else {
      setSortBy(null);
      setSortDir('desc');
    }
  };

  // Reusable sortable <th> renderer keeps the markup compact and the
  // three-state visual identical across columns.
  const SortableTh = ({ col, label, align = 'right', isText = false, testid }) => {
    const active = sortBy === col;
    const Icon = !active ? ArrowUpDown : sortDir === 'asc' ? ArrowUp : ArrowDown;
    return (
      <th
        scope="col"
        className={`font-semibold py-1.5 ${align === 'left' ? 'pr-2 text-left' : align === 'right-first' ? 'text-right pl-2' : 'px-2 text-right'} select-none`}
      >
        <button
          type="button"
          onClick={() => onSortClick(col, isText)}
          className={`inline-flex items-center gap-1 ${align === 'left' ? '' : 'ml-auto'} hover:text-foreground transition-colors ${active ? 'text-foreground' : ''}`}
          title={`Sort by ${label}`}
          data-testid={testid || `movers-sort-${col}`}
          aria-pressed={active}
          aria-label={`Sort by ${label}${active ? ` (${sortDir})` : ''}`}
        >
          {label}
          <Icon className={`w-2.5 h-2.5 ${active ? 'opacity-100' : 'opacity-40'}`} />
        </button>
      </th>
    );
  };

  return (
    <div className="rounded-xl border border-fuchsia-500/45 dark:border-fuchsia-400/45 bg-card p-4 sm:p-5" data-testid="top-movers-widget">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Rocket className="w-[18px] h-[18px] shrink-0 text-fuchsia-500 dark:text-fuchsia-400" aria-hidden />
          <h3 className="text-[18px] font-semibold tracking-tight truncate">
            {dividendFilter === 'all' ? 'Top All Movers'
              : dividendFilter === 'non_dividend' ? 'Top Non-Dividend Movers'
              : 'Top Dividend Movers'}
          </h3>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          <button
            type="button"
            onClick={() => setDirection('gainers')}
            data-testid="movers-gainers-btn"
            className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition-colors flex items-center gap-1 ${direction === 'gainers' ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300' : 'text-muted-foreground hover:text-foreground'}`}
          >
            <TrendingUp className="w-3 h-3" /> Gainers
          </button>
          <button
            type="button"
            onClick={() => setDirection('losers')}
            data-testid="movers-losers-btn"
            className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition-colors flex items-center gap-1 ${direction === 'losers' ? 'bg-rose-500/20 text-rose-700 dark:text-rose-300' : 'text-muted-foreground hover:text-foreground'}`}
          >
            <TrendingDown className="w-3 h-3" /> Losers
          </button>
          {/* Round 94c (Feb 2026) — Leveraged filter checkbox. Default
              checked so the list looks identical to pre-94c. Unchecking
              hides 2x/3x ETFs from the visible 3/5/10. Server returns
              30 rows so even Top-10 has non-leveraged headroom. */}
          <label
            className="inline-flex items-center gap-1 ml-1 cursor-pointer select-none"
            data-testid="movers-leveraged-toggle"
            title="When unchecked, leveraged 2x/3x ETFs are hidden from the list"
          >
            <input
              type="checkbox"
              checked={includeLeveraged}
              onChange={(e) => setIncludeLeveraged(e.target.checked)}
              className="w-3.5 h-3.5 accent-fuchsia-500 cursor-pointer"
              data-testid="movers-leveraged-checkbox"
            />
            <span className="text-[11px] font-semibold text-muted-foreground hover:text-foreground transition-colors">
              Leveraged
            </span>
          </label>
          <div className="inline-flex rounded-md border border-border ml-1">
            {[3, 5, 10].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setLimit(n)}
                data-testid={`movers-limit-${n}`}
                className={`px-2 py-0.5 text-[11px] font-mono font-semibold transition-colors ${limit === n ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              >
                {n}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={handleManualRefresh}
            data-testid="movers-refresh-btn"
            className="p-1 text-muted-foreground hover:text-foreground transition-colors"
            title="Force refresh now (bypasses 60s cache)"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Round 95z — 3-way dividend filter (independent of the Stock
          Table's filter). Lives on its own row directly under the
          title because the original header row is already full of
          controls on phone view — adding it there would create a
          third row that wraps awkwardly. Both controls default to
          "dividend" but track independently — preference persisted
          to localStorage. Admin-only for "all" / "non_dividend". */}
      <div className="flex justify-start mb-2" data-testid="movers-dividend-filter-row">
        <DividendFilterSegmentedControl
          value={dividendFilter}
          onChange={setDividendFilter}
          isAdmin={isAdmin}
          instanceId="movers"
        />
      </div>

      {/* Round 95bo (Jun 2026) — Cache-TTL transparency hint. The widget
          shows snapshot data that's up to 60 seconds old; clicking a
          ticker opens the modal which does a real-time per-ticker fetch
          and can differ by a few cents. Surface that upfront so users
          aren't surprised by tiny discrepancies. */}
      <div
        className="mb-2 text-[10px] font-bold text-muted-foreground/80 italic"
        data-testid="movers-cache-hint"
      >
        Snapshot refresh every 60s · click a ticker for the live price.
      </div>

      {showSpinner ? (
        <div className="flex items-center justify-center py-6 text-muted-foreground text-xs">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading movers…
        </div>
      ) : showEmpty ? (
        <div className="text-center py-6 text-muted-foreground text-xs" data-testid="movers-empty">
          {marketStatus === 'open' || marketStatus === 'extended' || marketStatus === 'early'
            ? `No ${dividendFilter === 'non_dividend' ? 'non-dividend' : dividendFilter === 'all' ? '' : 'dividend '}movers right now.`
            : `No ${dividendFilter === 'non_dividend' ? 'non-dividend' : dividendFilter === 'all' ? '' : 'dividend '}movers right now — try again during US market hours.`}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="movers-table">
            <thead className="text-[10px] uppercase tracking-wide text-muted-foreground border-b border-border/40">
              <tr>
                <th scope="col" className="w-6 py-1.5" aria-label="Watchlist" />
                <SortableTh col="ticker" label="Ticker" align="left" isText />
                <SortableTh col="price" label="Price" />
                <SortableTh col="change_pct" label="% Move" />
                <SortableTh col="volume" label="Volume" />
                <SortableTh col="dividend_yield" label="Yield" align="right-first" />
              </tr>
            </thead>
            <tbody>
              {visibleMovers.map((m) => {
                const isPositive = (m.change_pct ?? 0) >= 0;
                const inWatchlist = watchlist.includes(m.ticker);
                return (
                  <tr
                    key={m.ticker}
                    onClick={() => onStockClick?.(m.ticker)}
                    className="border-b border-border/20 last:border-0 hover:bg-muted/40 cursor-pointer transition-colors [&>td]:align-top"
                    data-testid={`mover-row-${m.ticker}`}
                  >
                    <td className="py-2 pr-1 w-6">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onToggleWatchlist?.(m.ticker); }}
                        className="p-0.5 rounded hover:bg-muted/60 transition-colors block"
                        data-testid={`watchlist-star-${m.ticker}`}
                        title={inWatchlist ? 'Remove from watchlist' : 'Add to watchlist'}
                        aria-label={inWatchlist ? `Remove ${m.ticker} from watchlist` : `Add ${m.ticker} to watchlist`}
                        aria-pressed={inWatchlist}
                      >
                        <Star className={`w-4 h-4 transition-colors ${inWatchlist ? 'fill-yellow-500 text-yellow-500' : 'text-muted-foreground/40 hover:text-muted-foreground'}`} />
                      </button>
                    </td>
                    <td className="py-2 pr-2">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-mono font-bold text-sm" data-testid={`mover-ticker-${m.ticker}`}>{m.ticker}</span>
                        <NewsBadge ticker={m.ticker} newsTickers={newsTickers} />
                        {!m.in_db && (
                          <span
                            className="text-[9px] uppercase tracking-wide px-1 py-0.5 rounded bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 cursor-help"
                            title="Not yet in DiviDrip's database — flagged for admin review. Click row to view on the fly."
                          >
                            new
                          </span>
                        )}
                        {/* P107b (Feb 2026) — Squeeze Radar cross-link.
                            Works across gainers/losers/leveraged and
                            all three dividend-filter modes because it
                            reads the same `/dashboard/squeeze-radar`
                            feed the strip uses. */}
                        <SqueezeRadarPill ticker={m.ticker} />
                      </div>
                      <p className="text-[10px] text-muted-foreground truncate max-w-[180px]">{m.name}</p>
                    </td>
                    <td className="text-right py-2 px-2 font-mono">{fmtPrice(m.price)}</td>
                    <td className={`text-right py-2 px-2 font-mono font-semibold ${isPositive ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`} data-testid={`mover-change-${m.ticker}`}>
                      {fmtPct(m.change_pct)}
                    </td>
                    <td className="text-right py-2 px-2 font-mono text-muted-foreground" data-testid={`mover-volume-${m.ticker}`}>
                      {fmtVol(m.volume)}
                    </td>
                    <td className="text-right py-2 pl-2 font-mono text-emerald-700 dark:text-emerald-300" data-testid={`mover-yield-${m.ticker}`}>
                      {m.dividend_yield != null ? `${Number(m.dividend_yield).toFixed(2)}%` : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex items-center justify-between text-[10px] text-muted-foreground mt-2">
        <span>
          {dividendFilter === 'all' ? 'All stocks (div + non-div)'
            : dividendFilter === 'non_dividend' ? 'Non-dividend stocks only'
            : 'Dividend stocks only'}
          {' · Auto-refresh 60s · Source: Massive'}
        </span>
        {fetchedAt[direction] && (
          <span data-testid="movers-fetched-at" title={fetchedAt[direction]}>
            Updated {fmtTime(fetchedAt[direction])}
          </span>
        )}
      </div>
    </div>
  );
}
